import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, MousePointerClick, FileCheck, Percent, TrendingUp, Clock, ArrowUp, ArrowDown } from 'lucide-react';

interface OverviewMetric {
  title: string;
  value: string;
  change: number;
  changeLabel: string;
  icon: React.ElementType;
}

interface OverviewCardsProps {
  totalViews: number;
  formStarts: number;
  responses: number;
  completionRate: number;
  conversionRate: number;
  avgCompletionTime: number;
  prevTotalViews: number;
  prevFormStarts: number;
  prevResponses: number;
  prevCompletionRate: number;
  prevConversionRate: number;
  prevAvgCompletionTime: number;
}

function calcChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

function formatTime(seconds: number): string {
  if (seconds === 0) return 'N/A';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function OverviewCards(props: OverviewCardsProps) {
  const metrics: OverviewMetric[] = [
    {
      title: 'Form Views',
      value: props.totalViews.toLocaleString(),
      change: calcChange(props.totalViews, props.prevTotalViews),
      changeLabel: 'vs previous period',
      icon: Eye,
    },
    {
      title: 'Form Starts',
      value: props.formStarts.toLocaleString(),
      change: calcChange(props.formStarts, props.prevFormStarts),
      changeLabel: 'vs previous period',
      icon: MousePointerClick,
    },
    {
      title: 'Responses',
      value: props.responses.toLocaleString(),
      change: calcChange(props.responses, props.prevResponses),
      changeLabel: 'vs previous period',
      icon: FileCheck,
    },
    {
      title: 'Completion Rate',
      value: `${props.completionRate}%`,
      change: calcChange(props.completionRate, props.prevCompletionRate),
      changeLabel: 'vs previous period',
      icon: Percent,
    },
    {
      title: 'Conversion Rate',
      value: `${props.conversionRate}%`,
      change: calcChange(props.conversionRate, props.prevConversionRate),
      changeLabel: 'views → responses',
      icon: TrendingUp,
    },
    {
      title: 'Avg. Time to Complete',
      value: formatTime(props.avgCompletionTime),
      change: calcChange(props.avgCompletionTime, props.prevAvgCompletionTime),
      changeLabel: 'vs previous period',
      icon: Clock,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {metrics.map((m) => {
        const isPositive = m.change >= 0;
        // For time, lower is better
        const isGood = m.title === 'Avg. Time to Complete' ? !isPositive : isPositive;
        return (
          <Card key={m.title} className="relative overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {m.title}
              </CardTitle>
              <m.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">{m.value}</div>
              <div className="flex items-center gap-1 mt-1">
                {m.change !== 0 && (
                  <>
                    {isPositive ? (
                      <ArrowUp className={`h-3 w-3 ${isGood ? 'text-emerald-500' : 'text-red-500'}`} />
                    ) : (
                      <ArrowDown className={`h-3 w-3 ${isGood ? 'text-emerald-500' : 'text-red-500'}`} />
                    )}
                    <span className={`text-xs font-medium ${isGood ? 'text-emerald-500' : 'text-red-500'}`}>
                      {Math.abs(m.change)}%
                    </span>
                  </>
                )}
                <span className="text-xs text-muted-foreground">{m.changeLabel}</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
